import { useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  Star,
  CalendarCheck,
  Sparkles,
  ClipboardCheck,
  CheckCircle2,
  Check,
  Lock,
  Plus,
  Pencil,
  ChevronRight,
  AlertTriangle,
  FileText,
} from "lucide-react";

/**
 * Phase W1 — the single, role-aware Weekly Report WIZARD.
 *
 * ONE report workflow lives here. It replaces the Team page's old competing
 * surfaces (the standalone "Additional weekly context" card, the read-only
 * readiness card, and the "Review weekly report" row). The core product rule
 * is made structural by the step order:
 *
 *   Humans declare status → AI writes the narrative → the lead approves.
 *
 * ROLE SPLIT (proven in the server payload, not just hidden):
 *   - AUTHORITY (owner/manager/lead) sees the four-step wizard.
 *   - MEMBERS see ONLY a compact outcome card. The report `get` procedure
 *     already returns members exactly { status, approved } — no draft, no
 *     stale, no snapshot/fingerprint/generationMeta — so a member never
 *     RECEIVES wizard state to begin with.
 *
 * NUMERAL-FREE workflow copy: no counts / "X of Y" / "N selected" anywhere in
 * this file (enforced by weeklyReportWizard.copy.test.tsx). Dates/week labels
 * are allowed; the operational recipient count stays only in the authority
 * email dialog on the report page.
 *
 * W1 keeps the legacy Weekly Team Update as a TEMPORARY Step 2 bridge. W2 will
 * replace Step 2 with per-priority declared progress.
 */

type Role = "owner" | "manager" | "lead" | "member" | undefined;
const isAuthorityRole = (r: Role) => r === "owner" || r === "manager" || r === "lead";

type ReportGet =
  | RouterOutputs["weeklyTeamReports"]["get"]
  | null
  | undefined;

type ActiveDirection = { id: number; title: string };
type FocusRow = { teamPriorityId: number; note?: string | null };
type UpdateRow = NonNullable<RouterOutputs["weeklyTeamUpdates"]["get"]>;
type DeclarationRow = RouterOutputs["teamPriorityWeeklyDeclarations"]["list"][number];

export interface WeeklyReportWizardProps {
  teamId: number;
  weekStartKey: string;
  isAr: boolean;
  role: Role;
  /** Active Team Direction rows (already filtered to teamStatus === "active"). */
  activeDirections: ActiveDirection[];
  /** This week's Weekly Focus rows (shared; teamPriorityId + optional note). */
  weeklyFocus: FocusRow[];
  /** Phase W2 — this week's per-priority human declarations (shared; sanitized:
   *  teamPriorityId + status + note, never author identity). */
  declarations: DeclarationRow[];
  /** Legacy Weekly Team Update (or null) — W2 keeps it as OPTIONAL "Additional
   *  context", never the authoritative progress source and never gating. */
  weeklyUpdate: UpdateRow | null;
  /** The role-projected report.get result for the viewed week. */
  report: ReportGet;
}

const DECL_STATUSES = ["on_track", "needs_attention", "blocked", "completed_this_week"] as const;
type DeclStatus = (typeof DECL_STATUSES)[number];
const declStatusLabel = (s: DeclStatus, isAr: boolean) =>
  ({
    on_track: isAr ? "على المسار" : "On track",
    needs_attention: isAr ? "يحتاج انتباهًا" : "Needs attention",
    blocked: isAr ? "معطَّل" : "Blocked",
    completed_this_week: isAr ? "أُنجز هذا الأسبوع" : "Completed this week",
  })[s];
const declStatusTone = (s: DeclStatus) =>
  ({
    on_track: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    needs_attention: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
    blocked: "bg-orange-500/15 text-orange-800 dark:text-orange-300",
    completed_this_week: "bg-coral-soft/70 text-coral",
  })[s];

const UPDATE_STATUSES = ["on_track", "needs_attention", "blocked", "completed_this_week"] as const;
type UpdateStatus = (typeof UPDATE_STATUSES)[number];

export function WeeklyReportWizard(props: WeeklyReportWizardProps) {
  const { role } = props;
  // The server ALREADY split the payload; the client role hint only chooses
  // which UI to mount. A member payload carries no draft/stale keys to leak.
  return isAuthorityRole(role) ? (
    <AuthorityWizard {...props} />
  ) : (
    <MemberOutcomeCard isAr={props.isAr} report={props.report} weekStartKey={props.weekStartKey} />
  );
}

/* ============================ MEMBER ============================ */

function MemberOutcomeCard({
  isAr,
  report,
  weekStartKey,
}: {
  isAr: boolean;
  report: ReportGet;
  weekStartKey: string;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const approved = report?.status === "approved";
  return (
    <div
      data-testid="weekly-report-member-card"
      className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
    >
      <h2 className="font-display text-sm tracking-wide text-muted-foreground uppercase">
        {t("Weekly report", "التقرير الأسبوعي")}
      </h2>
      <div className="mt-2 flex items-center gap-2">
        <Badge
          variant="secondary"
          data-testid="weekly-report-member-status"
          className={cn(
            "text-[11px]",
            approved
              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "bg-muted text-muted-foreground",
          )}
        >
          {approved ? t("Approved", "معتمد") : t("In preparation", "قيد الإعداد")}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground" data-testid="weekly-report-member-note">
        {approved
          ? t("Visible to the team.", "مرئي للفريق.")
          : t("The team’s weekly report is being prepared.", "يجري إعداد التقرير الأسبوعي للفريق.")}
      </p>
      {approved ? (
        <a
          href={`/team-report?week=${weekStartKey}`}
          data-testid="weekly-report-member-view"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          {t("View approved report", "عرض التقرير المعتمد")}
          <ChevronRight className="h-4 w-4" />
        </a>
      ) : null}
    </div>
  );
}

/* ========================== AUTHORITY ========================== */

type StepKey = "focus" | "declare" | "generate" | "review";
type StepState = "completed" | "current" | "locked";

function AuthorityWizard({
  teamId,
  weekStartKey,
  isAr,
  activeDirections,
  weeklyFocus,
  declarations,
  weeklyUpdate,
  report,
}: WeeklyReportWizardProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  // ---- MANDATORY empty-direction gate: no runnable steps exist without an
  // active Team Direction. This makes the impossible "no directions + focus
  // rows" state structurally unreachable. ----
  if (activeDirections.length === 0) {
    return (
      <div
        data-testid="weekly-report-empty-direction-gate"
        className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
      >
        <h2 className="font-display text-lg">{t("Set Team Direction first", "حدِّد توجيه الفريق أولًا")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "Add at least one active team priority before preparing this week’s report.",
            "أضف أولوية فريق نشطة واحدة على الأقل قبل إعداد تقرير هذا الأسبوع.",
          )}
        </p>
        <Button
          className="mt-3 rounded-full"
          data-testid="weekly-report-add-direction"
          onClick={() => {
            const el = document.querySelector<HTMLElement>('[data-testid="td-create"]');
            el?.click();
            el?.scrollIntoView({ block: "center" });
          }}
        >
          <Plus className="h-4 w-4 me-1.5" /> {t("Add team priority", "أضف أولوية فريق")}
        </Button>
      </div>
    );
  }

  // ---- step completion (numeral-free booleans, never counts) ----
  const focusSet = weeklyFocus.length > 0;
  const focusedDirections = activeDirections.filter(d =>
    weeklyFocus.some(f => f.teamPriorityId === d.id),
  );
  const declByPriority = new Map(declarations.map(x => [x.teamPriorityId, x] as const));
  // W2 — the human-declaration step (and generation) is complete only when
  // EVERY focused direction carries exactly one declaration. This is the
  // STRUCTURAL generate gate: the declaration step is unskippable.
  const declarationsComplete =
    focusedDirections.length > 0 && focusedDirections.every(d => declByPriority.has(d.id));
  const reportStatus = report?.status;
  const hasDraftOrApproved =
    reportStatus === "draft" ||
    reportStatus === "approved" ||
    reportStatus === "approved_with_draft";
  // A current DRAFT exists (so Generate must REPLACE it, not create_only).
  const hasCurrentDraft =
    reportStatus === "draft" || reportStatus === "approved_with_draft";
  // Phase L — the SANITIZED editability signal (authority get). true before
  // generation and while an explicit editable cycle is open; false once inputs
  // were used to generate the current draft/approved report.
  const canEditInputs: boolean =
    report && "canEditReportInputs" in report ? !!report.canEditReportInputs : true;

  const stepDone: Record<StepKey, boolean> = {
    focus: focusSet,
    declare: declarationsComplete,
    // While an editable cycle is open, Generate is the ACTION that produces the
    // new draft — an old approved/draft revision does NOT mark the step done.
    generate: hasDraftOrApproved && !canEditInputs,
    review: reportStatus === "approved" || reportStatus === "approved_with_draft",
  };
  const order: StepKey[] = ["focus", "declare", "generate", "review"];
  const currentStep: StepKey =
    order.find(k => !stepDone[k]) ?? "review";
  const stateOf = (k: StepKey): StepState => {
    if (stepDone[k]) return "completed";
    if (k === currentStep) return "current";
    return "locked";
  };

  const focusedTitles = focusedDirections.map(d => d.title);

  const invalidate = () => {
    utils.weeklyFocus.list.invalidate();
    utils.weeklyTeamUpdates.get.invalidate();
    utils.teamPriorityWeeklyDeclarations.list.invalidate();
    utils.weeklyTeamReports.get.invalidate();
  };
  // Lock-aware error: a rejected input write while locked (a race — another
  // authority generated while this editor was open) gets the safe locked
  // message and a resync; everything else stays the neutral failure.
  const onError = (e?: unknown) => {
    if ((e as { message?: string })?.message === "weekly_report_inputs_locked") {
      toast.error(
        t(
          "This report’s inputs are locked. Create an updated draft to make changes.",
          "مدخلات هذا التقرير مقفلة. أنشئ مسودة محدَّثة لإجراء تغييرات.",
        ),
      );
      invalidate(); // reconcile with server truth; do not retry the stale write
      return;
    }
    toast.error(t("Couldn’t complete that.", "تعذّر تنفيذ العملية."));
  };

  // Phase L — "Previous progress restored" ids: a priority RE-ADDED to focus
  // that already carries a retained declaration. Session-only (never persisted),
  // cleared once the lead edits that declaration in the current editable cycle.
  const [restoredIds, setRestoredIds] = useState<Set<number>>(() => new Set());
  const noteRestored = (addedIds: number[]) =>
    setRestoredIds(prev => {
      const next = new Set(prev);
      for (const id of addedIds) if (declByPriority.has(id)) next.add(id);
      return next;
    });
  const clearRestored = (id: number) =>
    setRestoredIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Phase L — "Create updated draft": opens an editable input cycle WITHOUT
  // generating. Approved stays visible; the server never unapproves/deletes.
  const openCycle = trpc.weeklyTeamReports.openUpdatedDraftCycle.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(
        t(
          "Inputs unlocked. Edit focus and progress, then generate an updated draft.",
          "تم فتح المدخلات. عدّل التركيز والتقدّم ثم أنشئ مسودة محدَّثة.",
        ),
      );
    },
    onError: () => onError(),
  });
  const createUpdatedDraft = () => openCycle.mutate({ teamId, weekStartKey });

  return (
    <section
      data-testid="weekly-report-wizard"
      aria-label={t("Weekly report", "التقرير الأسبوعي")}
      className="rounded-3xl border border-border/60 bg-card p-4 sm:p-5 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-display text-lg">{t("Weekly report", "التقرير الأسبوعي")}</h2>
        {/* desktop horizontal stepper — labels only, NO numerals */}
        <ol className="hidden sm:flex items-center gap-1.5" data-testid="weekly-report-stepper" aria-hidden>
          {order.map(k => (
            <li
              key={k}
              className={cn(
                "h-1.5 w-6 rounded-full",
                stateOf(k) === "completed"
                  ? "bg-emerald-500"
                  : stateOf(k) === "current"
                    ? "bg-primary"
                    : "bg-muted",
              )}
            />
          ))}
        </ol>
      </div>

      <div className="space-y-2.5">
        <FocusStep
          state={stateOf("focus")}
          isAr={isAr}
          teamId={teamId}
          weekStartKey={weekStartKey}
          activeDirections={activeDirections}
          weeklyFocus={weeklyFocus}
          focusedTitles={focusedTitles}
          canEditInputs={canEditInputs}
          onCreateUpdatedDraft={createUpdatedDraft}
          creatingCycle={openCycle.isPending}
          onSavedFocus={noteRestored}
          onChanged={invalidate}
          onError={onError}
        />
        <DeclareStep
          state={stateOf("declare")}
          isAr={isAr}
          teamId={teamId}
          weekStartKey={weekStartKey}
          focusedDirections={focusedDirections}
          declByPriority={declByPriority}
          weeklyUpdate={weeklyUpdate}
          canEditInputs={canEditInputs}
          restoredIds={restoredIds}
          onDeclarationEdited={clearRestored}
          onCreateUpdatedDraft={createUpdatedDraft}
          creatingCycle={openCycle.isPending}
          onChanged={invalidate}
          onError={onError}
        />
        <GenerateStep
          state={stateOf("generate")}
          isAr={isAr}
          teamId={teamId}
          weekStartKey={weekStartKey}
          regenerate={hasCurrentDraft}
          onGenerated={mode => {
            invalidate();
            if (mode === "deterministic") return; // fallback message stays; lead taps Continue
            // The freshly generated draft IS the updated draft — return to the
            // report page defaulting to the draft view (approved stays reachable
            // via the view switch). Week preserved.
            setLocation(`/team-report?week=${weekStartKey}&view=draft`);
          }}
          onOpenReview={() => setLocation(`/team-report?week=${weekStartKey}&view=draft`)}
        />
        <ReviewStep
          state={stateOf("review")}
          hasDraftOrApproved={hasDraftOrApproved}
          approved={reportStatus === "approved" || reportStatus === "approved_with_draft"}
          isAr={isAr}
          onOpenReview={() => setLocation(`/team-report?week=${weekStartKey}`)}
        />
      </div>
    </section>
  );
}

/* --------------------------- step shell --------------------------- */

function StepShell({
  stepKey,
  state,
  icon,
  title,
  children,
}: {
  stepKey: StepKey;
  state: StepState;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={`weekly-report-step-${stepKey}`}
      data-state={state}
      className={cn(
        "rounded-2xl border p-3.5 transition",
        state === "current"
          ? "border-primary/40 bg-primary/[0.03]"
          : state === "completed"
            ? "border-border/60 bg-muted/20"
            : "border-border/40 bg-muted/10 opacity-70",
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "grid h-6 w-6 shrink-0 place-items-center rounded-full",
            state === "completed"
              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
              : state === "current"
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground",
          )}
        >
          {state === "completed" ? (
            <CheckCircle2 className="h-4 w-4" data-testid={`weekly-report-step-${stepKey}-done`} />
          ) : state === "locked" ? (
            <Lock className="h-3.5 w-3.5" data-testid={`weekly-report-step-${stepKey}-lock`} />
          ) : (
            icon
          )}
        </span>
        <h3 className="text-sm font-medium min-w-0">{title}</h3>
      </div>
      <div className="mt-2 ps-8">{children}</div>
    </div>
  );
}

/* --------------------------- Step 1: Focus --------------------------- */

function FocusStep({
  state,
  isAr,
  teamId,
  weekStartKey,
  activeDirections,
  weeklyFocus,
  focusedTitles,
  canEditInputs,
  onCreateUpdatedDraft,
  creatingCycle,
  onSavedFocus,
  onChanged,
  onError,
}: {
  state: StepState;
  isAr: boolean;
  teamId: number;
  weekStartKey: string;
  activeDirections: ActiveDirection[];
  weeklyFocus: FocusRow[];
  focusedTitles: string[];
  canEditInputs: boolean;
  onCreateUpdatedDraft: () => void;
  creatingCycle: boolean;
  onSavedFocus: (addedIds: number[]) => void;
  onChanged: () => void;
  onError: (e?: unknown) => void;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  // SAVED focus = the currently persisted selection (derived from props).
  const savedFocusIds = useMemo(
    () => new Set(weeklyFocus.map(f => f.teamPriorityId)),
    [weeklyFocus],
  );
  // Explicit, reversible edit mode. DRAFT is a separate local selection; nothing
  // persists until the human presses Save focus. Selecting/deselecting a row
  // never mutates and never closes the editor. Opening the editor enters this
  // temporary state initialized FROM the saved focus (never auto-saved). When no
  // focus is saved yet, the editor opens so the lead can pick + save.
  const [editing, setEditing] = useState(savedFocusIds.size === 0);
  const [draftFocusIds, setDraftFocusIds] = useState<Set<number>>(
    () => new Set(savedFocusIds),
  );
  const [saving, setSaving] = useState(false);
  // Synchronous re-entry guard: a ref flips before React re-renders the disabled
  // button, so two rapid taps can't fire the diff twice.
  const savingRef = useRef(false);
  // No onSuccess/onError here — Save coordinates the whole diff and decides when
  // to invalidate/close, so a per-mutation callback can't half-close the editor.
  const setFocus = trpc.weeklyFocus.set.useMutation();
  const removeFocus = trpc.weeklyFocus.remove.useMutation();

  const openEditor = () => {
    setDraftFocusIds(new Set(savedFocusIds)); // draft := saved
    setEditing(true);
  };
  const cancel = () => {
    setDraftFocusIds(new Set(savedFocusIds)); // discard unsaved draft
    setEditing(false); // no mutation
  };
  const toggleDraft = (id: number) =>
    setDraftFocusIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearDraft = () => setDraftFocusIds(new Set()); // draft only; Save confirms
  const save = async () => {
    if (savingRef.current) return; // guard against duplicate mutations from rapid taps
    const adds = [...draftFocusIds].filter(id => !savedFocusIds.has(id));
    const removes = [...savedFocusIds].filter(id => !draftFocusIds.has(id));
    if (adds.length === 0 && removes.length === 0) {
      setEditing(false); // no change → just close (saving zero stays a no-op)
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      // No atomic replace exists; coordinate the diff through the existing
      // set/remove mutations and wait for ALL to settle before closing.
      await Promise.all([
        ...adds.map(id => setFocus.mutateAsync({ teamId, weekStartKey, teamPriorityId: id })),
        ...removes.map(id => removeFocus.mutateAsync({ teamId, weekStartKey, teamPriorityId: id })),
      ]);
      onSavedFocus(adds); // a re-added priority with a retained declaration → "restored"
      onChanged(); // invalidate → refetch the saved focus
      setEditing(false);
    } catch (e) {
      onError(e);
      onChanged(); // resync saved state; keep the editor OPEN for a safe retry
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <StepShell stepKey="focus" state={state} icon={<Star className="h-3.5 w-3.5" />} title={t("Set this week’s focus", "حدِّد تركيز هذا الأسبوع")}>
      {!canEditInputs ? (
        // Phase L — LOCKED: these priorities were used to generate the current
        // draft. Read-only titles, no Edit focus; the ONE unlock action is
        // Create updated draft (opens an editable cycle without generating).
        <div data-testid="weekly-report-focus-locked">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5" data-testid="weekly-report-focus-locked-title">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {t("Focus locked for this draft", "التركيز مقفل لهذه المسودة")}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {t("These priorities were used to create the current draft.", "استُخدمت هذه الأولويات لإنشاء المسودة الحالية.")}
          </p>
          {focusedTitles.length > 0 ? (
            <ul className="mt-1.5 space-y-0.5" data-testid="weekly-report-focus-saved-titles">
              {focusedTitles.map((title, i) => (
                <li key={i} className="text-sm break-words flex items-start gap-1.5">
                  <Star aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral fill-current" />
                  <span className="min-w-0 break-words">{title}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <Button
            size="sm"
            className="mt-2.5 rounded-full"
            data-testid="weekly-report-create-updated-draft"
            disabled={creatingCycle}
            onClick={onCreateUpdatedDraft}
          >
            <Pencil className="h-3.5 w-3.5 me-1.5" />
            {t("Create updated draft", "إنشاء مسودة محدَّثة")}
          </Button>
        </div>
      ) : editing ? (
        <div>
          <p className="text-xs text-muted-foreground mb-2">
            {t("Choose the active directions that matter this week.", "اختر التوجيهات النشطة المهمة هذا الأسبوع.")}
          </p>
          <ul
            className="space-y-1.5"
            data-testid="weekly-report-focus-picker"
            role="group"
            aria-label={t("Set this week’s focus", "حدِّد تركيز هذا الأسبوع")}
          >
            {activeDirections.map(d => {
              const on = draftFocusIds.has(d.id);
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    data-testid={`weekly-report-focus-option-${d.id}`}
                    aria-pressed={on}
                    onClick={() => toggleDraft(d.id)}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-xl border px-3 py-2 text-start transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                      on
                        ? "border-coral/40 bg-coral-soft/40"
                        : "border-border/60 hover:border-border",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border",
                        on ? "bg-coral text-white border-coral" : "border-border/70",
                      )}
                    >
                      {on ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <Star
                      aria-hidden
                      className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", on ? "text-coral fill-current" : "text-muted-foreground")}
                    />
                    <span className={cn("text-sm break-words min-w-0", on ? "font-medium" : "")}>
                      {d.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {/* Explicit, clearly visible action area. Save is the primary action;
              Cancel discards; Clear focus (only when something is selected) empties
              the draft but still requires Save to confirm. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {draftFocusIds.size > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                className="me-auto rounded-full text-muted-foreground"
                data-testid="weekly-report-focus-clear"
                disabled={saving}
                onClick={clearDraft}
              >
                {t("Clear focus", "مسح التركيز")}
              </Button>
            ) : (
              <span className="me-auto" />
            )}
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              data-testid="weekly-report-focus-cancel"
              disabled={saving}
              onClick={cancel}
            >
              {t("Cancel", "إلغاء")}
            </Button>
            <Button
              size="sm"
              className="rounded-full min-w-[6.5rem]"
              data-testid="weekly-report-focus-save"
              disabled={saving}
              onClick={save}
            >
              {t("Save focus", "حفظ التركيز")}
            </Button>
          </div>
        </div>
      ) : savedFocusIds.size > 0 ? (
        <div>
          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium" data-testid="weekly-report-focus-summary">
            {t("Focus set", "تم تحديد التركيز")}
          </p>
          <ul className="mt-1 space-y-0.5" data-testid="weekly-report-focus-saved-titles">
            {focusedTitles.map((title, i) => (
              <li key={i} className="text-sm break-words flex items-start gap-1.5">
                <Star aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral fill-current" />
                <span className="min-w-0 break-words">{title}</span>
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            variant="ghost"
            className="mt-2 rounded-full text-muted-foreground"
            data-testid="weekly-report-focus-edit"
            onClick={openEditor}
          >
            <Pencil className="h-3.5 w-3.5 me-1" /> {t("Edit focus", "تعديل التركيز")}
          </Button>
        </div>
      ) : (
        <div>
          <p className="text-xs text-muted-foreground font-medium" data-testid="weekly-report-focus-summary">
            {t("Focus not set", "لم يُحدَّد التركيز")}
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="mt-2 rounded-full text-muted-foreground"
            data-testid="weekly-report-focus-edit"
            onClick={openEditor}
          >
            <Pencil className="h-3.5 w-3.5 me-1" /> {t("Edit focus", "تعديل التركيز")}
          </Button>
        </div>
      )}
    </StepShell>
  );
}

/* ------------------------ Step 2 (W1): Update ------------------------ */

const updateStatusLabel = (s: UpdateStatus, isAr: boolean) =>
  ({
    on_track: isAr ? "على المسار" : "On track",
    needs_attention: isAr ? "يحتاج انتباهًا" : "Needs attention",
    blocked: isAr ? "معطَّل" : "Blocked",
    completed_this_week: isAr ? "أُنجز هذا الأسبوع" : "Completed this week",
  })[s];

type DeclarationWire = { teamPriorityId: number; status: string; note: string | null; blocker: string | null; nextStep: string | null; decisionRequired: string | null };

function DeclareStep({
  state,
  isAr,
  teamId,
  weekStartKey,
  focusedDirections,
  declByPriority,
  weeklyUpdate,
  canEditInputs,
  restoredIds,
  onDeclarationEdited,
  onCreateUpdatedDraft,
  creatingCycle,
  onChanged,
  onError,
}: {
  state: StepState;
  isAr: boolean;
  teamId: number;
  weekStartKey: string;
  focusedDirections: ActiveDirection[];
  declByPriority: Map<number, DeclarationRow>;
  weeklyUpdate: UpdateRow | null;
  canEditInputs: boolean;
  restoredIds: Set<number>;
  onDeclarationEdited: (id: number) => void;
  onCreateUpdatedDraft: () => void;
  creatingCycle: boolean;
  onChanged: () => void;
  onError: (e?: unknown) => void;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [busyId, setBusyId] = useState<number | null>(null);
  const upsert = trpc.teamPriorityWeeklyDeclarations.upsert.useMutation({ onSuccess: onChanged, onError: e => onError(e) });
  // The wizard edits status + optional note; blocker/next-step/decision are
  // legacy fields carried through so a status/note edit never wipes them.
  const write = (dir: ActiveDirection, status: DeclStatus, note: string | null) => {
    const e = declByPriority.get(dir.id) as DeclarationWire | undefined;
    onDeclarationEdited(dir.id); // an explicit edit clears the "restored" notice
    setBusyId(dir.id);
    upsert.mutate(
      {
        teamId,
        weekStartKey,
        teamPriorityId: dir.id,
        status,
        note,
        blocker: e?.blocker ?? null,
        nextStep: e?.nextStep ?? null,
        decisionRequired: e?.decisionRequired ?? null,
      },
      { onSettled: () => setBusyId(null) },
    );
  };

  return (
    <StepShell stepKey="declare" state={state} icon={<ClipboardCheck className="h-3.5 w-3.5" />} title={t("Declare progress for focused priorities", "أعلِن التقدّم للأولويات المُركَّزة")}>
      {state === "locked" ? (
        // Step 2 is DISABLED until a focus is saved. Explain why — never a bare
        // gray card. (Step 2 is only locked when the saved focus is empty.)
        <p className="text-xs text-muted-foreground" data-testid="weekly-report-declare-locked-note">
          {t("Select and save this week’s focus first.", "اختر واحفظ تركيز هذا الأسبوع أولًا.")}
        </p>
      ) : !canEditInputs ? (
        // Phase L — LOCKED after generation: the declarations that built the
        // current draft are read-only. No pickers, no notes editor. Changing
        // them means opening a new cycle (Create updated draft, in Step 1).
        <div data-testid="weekly-report-declare-inputs-locked">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5" data-testid="weekly-report-declare-locked-title">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {t("Progress locked for this draft", "التقدّم مقفل لهذه المسودة")}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {t(
              "These declarations were used to create the current draft. Create an updated draft to change them.",
              "استُخدمت هذه الإعلانات لإنشاء المسودة الحالية. أنشئ مسودة محدَّثة لتغييرها.",
            )}
          </p>
          <p className="mt-2.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="weekly-report-declare-section">
            {t("Focused priorities", "الأولويات المُركَّزة")}
          </p>
          <ul className="mt-1.5 space-y-3">
            {focusedDirections.map(dir => {
              const decl = declByPriority.get(dir.id);
              return (
                <li key={dir.id} data-testid={`weekly-report-declare-item-${dir.id}`}>
                  <p className="text-sm font-medium break-words flex items-start gap-1.5">
                    <Star aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral fill-current" />
                    <span className="min-w-0 break-words">{dir.title}</span>
                  </p>
                  {decl ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={cn("text-[11px]", declStatusTone(decl.status as DeclStatus))}
                        data-testid={`weekly-report-declare-status-${dir.id}`}
                      >
                        {declStatusLabel(decl.status as DeclStatus, isAr)}
                      </Badge>
                      {decl.note ? (
                        <span className="text-sm text-muted-foreground break-words">{decl.note}</span>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div>
          {state === "completed" ? (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium" data-testid="weekly-report-declare-summary">
              {t("Progress declared", "تم إعلان التقدّم")}
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground" data-testid="weekly-report-declare-subtitle">
                {t(
                  "Set a status for each priority selected in this week’s focus.",
                  "عيّن حالة لكل أولوية اخترتها في تركيز هذا الأسبوع.",
                )}
              </p>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="weekly-report-declare-trust">
                {t("You choose each status. The system never picks it.", "أنت تختار كل حالة. النظام لا يختارها أبدًا.")}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="weekly-report-declare-receipts-note">
                {t(
                  "Approved receipts are added separately when the report is generated.",
                  "تُضاف الملخّصات المعتمدة بشكل منفصل عند إنشاء التقرير.",
                )}
              </p>
              {focusedDirections.some(d => !declByPriority.has(d.id)) ? (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400" data-testid="weekly-report-declare-incomplete">
                  {t(
                    "Declare a status for each focused priority to continue.",
                    "أعلِن حالة لكل أولوية مُركَّزة للمتابعة.",
                  )}
                </p>
              ) : null}
            </>
          )}
          {/* FOCUSED PRIORITIES — declarations belong to the directions saved in
              Step 1 (same focus/star treatment), never to Shared Receipts. Every
              declaration stays EDITABLE before generation (even once all are
              declared) — the completed state is a progress hint, never a lock. */}
          <p className="mt-2.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="weekly-report-declare-section">
            {t("Focused priorities", "الأولويات المُركَّزة")}
          </p>
          <ul className="mt-1.5 space-y-3">
            {focusedDirections.map(dir => {
              const decl = declByPriority.get(dir.id);
              return (
                <li key={dir.id} data-testid={`weekly-report-declare-item-${dir.id}`}>
                  <p className="text-sm font-medium break-words flex items-start gap-1.5">
                    <Star aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral fill-current" />
                    <span className="min-w-0 break-words">{dir.title}</span>
                  </p>
                  {/* Phase L — a RE-ADDED priority that already carried a
                      declaration: a non-blocking notice, cleared the moment the
                      lead edits it. Never a fresh re-confirmation, never a lock. */}
                  {restoredIds.has(dir.id) ? (
                    <div className="mt-1" data-testid={`weekly-report-declare-restored-${dir.id}`}>
                      <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                        {t("Previous progress restored", "استُعيد التقدّم السابق")}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {t("Review or change it before generating the report.", "راجعه أو غيّره قبل إنشاء التقرير.")}
                      </p>
                    </div>
                  ) : null}
                  {/* Mobile-safe 2×2 status grid — the primary interaction. */}
                  <div
                    className="mt-1.5 grid grid-cols-2 gap-1.5"
                    role="group"
                    aria-label={t("Status", "الحالة")}
                    data-testid={`weekly-report-declare-picker-${dir.id}`}
                  >
                    {DECL_STATUSES.map(s => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`weekly-report-declare-status-${dir.id}-${s}`}
                        aria-pressed={decl?.status === s}
                        disabled={busyId === dir.id}
                        onClick={() => write(dir, s, decl?.note ?? null)}
                        className={cn(
                          "min-h-9 rounded-xl px-3 py-2 text-xs font-medium border transition text-center",
                          decl?.status === s
                            ? cn(declStatusTone(s), "border-transparent")
                            : "border-border/60 text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {declStatusLabel(s, isAr)}
                      </button>
                    ))}
                  </div>
                  {decl ? (
                    <input
                      key={`n-${dir.id}-${decl.note ?? ""}`}
                      defaultValue={decl.note ?? ""}
                      maxLength={280}
                      placeholder={t("Add a short note (optional)", "أضف ملاحظة قصيرة (اختياري)")}
                      data-testid={`weekly-report-declare-note-${dir.id}`}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          write(dir, decl.status as DeclStatus, e.currentTarget.value.trim() || null);
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={e => {
                        const v = e.currentTarget.value.trim() || null;
                        if (v !== (decl.note ?? null)) write(dir, decl.status as DeclStatus, v);
                      }}
                      className="mt-1.5 w-full rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs"
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
          {/* Legacy Weekly Team Update — OPTIONAL "Additional context" only.
              Never the authoritative progress source, never gating. */}
          <AdditionalContextSection
            isAr={isAr}
            teamId={teamId}
            weekStartKey={weekStartKey}
            update={weeklyUpdate}
            onChanged={onChanged}
            onError={onError}
          />
        </div>
      )}
    </StepShell>
  );
}

/** Optional legacy Weekly Team Update, kept as a collapsed "Additional context"
 *  disclosure inside Step 2. Not required, not the primary progress source. */
function AdditionalContextSection({
  isAr,
  teamId,
  weekStartKey,
  update,
  onChanged,
  onError,
}: {
  isAr: boolean;
  teamId: number;
  weekStartKey: string;
  update: UpdateRow | null;
  onChanged: () => void;
  onError: () => void;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    status: (update?.status as UpdateStatus) ?? "on_track",
    summary: update?.summary ?? "",
    blocker: update?.blocker ?? "",
    nextStep: update?.nextStep ?? "",
  });
  const set = trpc.weeklyTeamUpdates.set.useMutation({
    onSuccess: () => {
      onChanged();
      setEditing(false);
    },
    onError,
  });
  const open = () => {
    setDraft({
      status: (update?.status as UpdateStatus) ?? "on_track",
      summary: update?.summary ?? "",
      blocker: update?.blocker ?? "",
      nextStep: update?.nextStep ?? "",
    });
    setEditing(true);
  };
  const save = () => {
    const summary = draft.summary.trim();
    if (!summary) return;
    set.mutate({
      teamId,
      weekStartKey,
      status: draft.status,
      summary,
      blocker: draft.blocker.trim() || null,
      nextStep: draft.nextStep.trim() || null,
    });
  };
  return (
    <details className="mt-3 rounded-2xl border border-border/50 bg-muted/20 p-3" data-testid="weekly-report-additional-context">
      <summary className="cursor-pointer list-none text-xs font-medium text-muted-foreground marker:hidden">
        {t("Additional context (optional)", "سياق إضافي (اختياري)")}
      </summary>
      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="weekly-report-additional-context-note">
        {t(
          "Optional shared context. It is not the team’s progress status and is not required to generate the report.",
          "سياق مُشارَك اختياري. ليس حالة تقدّم الفريق وغير مطلوب لإنشاء التقرير.",
        )}
      </p>
      {update && !editing ? (
        <div className="mt-2" data-testid="weekly-report-additional-context-summary">
          <p className="text-sm whitespace-pre-wrap break-words">{update.summary}</p>
          <Button size="sm" variant="ghost" className="mt-1 rounded-full text-muted-foreground" data-testid="weekly-report-additional-context-edit" onClick={open}>
            <Pencil className="h-3.5 w-3.5 me-1" /> {t("Edit context", "تعديل السياق")}
          </Button>
        </div>
      ) : editing ? (
        <div className="mt-2 space-y-2.5" data-testid="weekly-report-additional-context-editor">
          <Textarea
            data-testid="weekly-report-additional-context-input"
            value={draft.summary}
            maxLength={2000}
            rows={2}
            placeholder={t("Add optional shared context.", "أضف سياقًا مُشارَكًا اختياريًا.")}
            onChange={e => setDraft(d => ({ ...d, summary: e.target.value }))}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full" data-testid="weekly-report-additional-context-save" disabled={set.isPending || draft.summary.trim().length === 0} onClick={save}>
              {t("Save context", "حفظ السياق")}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full" data-testid="weekly-report-additional-context-cancel" onClick={() => setEditing(false)}>
              {t("Cancel", "إلغاء")}
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="ghost" className="mt-2 rounded-full text-muted-foreground" data-testid="weekly-report-additional-context-add" onClick={open}>
          <Plus className="h-3.5 w-3.5 me-1" /> {t("Add context", "أضف سياقًا")}
        </Button>
      )}
    </details>
  );
}

/* ------------------------- Step 3: Generate ------------------------- */

function GenerateStep({
  state,
  isAr,
  teamId,
  weekStartKey,
  regenerate,
  onGenerated,
  onOpenReview,
}: {
  state: StepState;
  isAr: boolean;
  teamId: number;
  weekStartKey: string;
  /** Phase L — a current draft already exists (open cycle): REPLACE it. */
  regenerate: boolean;
  onGenerated: (mode: "llm" | "deterministic") => void;
  onOpenReview: () => void;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [phase, setPhase] = useState<"idle" | "fallback" | "failed">("idle");
  const generate = trpc.weeklyTeamReports.generate.useMutation({
    onSuccess: res => {
      if (res && "ok" in res && res.ok === false) {
        setPhase("failed");
        return;
      }
      const mode = (res as { generationMode?: "llm" | "deterministic" })?.generationMode ?? "llm";
      if (mode === "deterministic") setPhase("fallback");
      onGenerated(mode);
    },
    onError: () => setPhase("failed"),
  });
  const run = () => {
    setPhase("idle");
    generate.mutate({ teamId, weekStartKey, regenerate });
  };

  return (
    <StepShell stepKey="generate" state={state} icon={<Sparkles className="h-3.5 w-3.5" />} title={t("Generate draft", "إنشاء المسودة")}>
      {state === "completed" ? (
        <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium" data-testid="weekly-report-generate-summary">
          {t("Draft ready", "المسودة جاهزة")}
        </p>
      ) : state === "locked" ? (
        <p className="text-xs text-muted-foreground" data-testid="weekly-report-generate-locked-note">
          {t("Declare progress for each focused priority first.", "أعلِن التقدّم لكل أولوية مُركَّزة أولًا.")}
        </p>
      ) : phase === "failed" ? (
        <div data-testid="weekly-report-generate-failed">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm">{t("We couldn’t generate the report. Try again.", "تعذّر إنشاء التقرير. حاول مرة أخرى.")}</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full" data-testid="weekly-report-generate-retry" disabled={generate.isPending} onClick={run}>
              {t("Try again", "حاول مرة أخرى")}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full" data-testid="weekly-report-generate-back" onClick={() => setPhase("idle")}>
              {t("Return to inputs", "العودة إلى المدخلات")}
            </Button>
          </div>
        </div>
      ) : phase === "fallback" ? (
        <div data-testid="weekly-report-generate-fallback">
          <p className="text-sm">{t("AI draft could not be used. A basic draft was created instead.", "تعذّر استخدام مسودة الذكاء الاصطناعي. تم إنشاء مسودة أساسية بدلًا من ذلك.")}</p>
          <Button size="sm" className="mt-2 rounded-full" data-testid="weekly-report-generate-continue" onClick={onOpenReview}>
            {t("Continue to review", "المتابعة إلى المراجعة")}
          </Button>
        </div>
      ) : (
        <div>
          <p className="text-xs text-muted-foreground">{t("The report will use:", "سيستخدم التقرير:")}</p>
          <div className="mt-1.5" data-testid="weekly-report-generate-sources">
            {/* Human + priority sources — same focus/star treatment as Steps 1–2
                and a status icon for the human declarations. */}
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                <Star aria-hidden className="h-3.5 w-3.5 shrink-0 text-coral" />
                {t("Team Direction", "توجيه الفريق")}
              </li>
              <li className="flex items-center gap-1.5">
                <Star aria-hidden className="h-3.5 w-3.5 shrink-0 text-coral" />
                {t("This week’s focus", "تركيز هذا الأسبوع")}
              </li>
              <li className="flex items-center gap-1.5" data-testid="weekly-report-source-declared">
                <ClipboardCheck aria-hidden className="h-3.5 w-3.5 shrink-0" />
                {t("Declared progress for focused priorities", "التقدّم المُعلَن للأولويات المُركَّزة")}
              </li>
            </ul>
            {/* Approved receipts — SEPARATE supporting evidence, not a priority
                progress row. Distinct document icon + explicit separation copy,
                so the UI never implies progress is derived from receipts. */}
            <div className="mt-2 border-t border-border/50 pt-2" data-testid="weekly-report-generate-receipts">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FileText aria-hidden className="h-3.5 w-3.5 shrink-0" />
                {t("Approved receipts", "الملخّصات المعتمدة")}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground ps-5" data-testid="weekly-report-generate-receipts-note">
                {t(
                  "Approved receipts are separate supporting evidence for the report.",
                  "الملخّصات المعتمدة دليل داعم منفصل للتقرير.",
                )}
              </p>
            </div>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="weekly-report-generate-trust">
            {t("Nothing private is used.", "لا يُستخدم أي شيء خاص.")}
          </p>
          <Button className="mt-2.5 rounded-full" data-testid="weekly-report-generate" disabled={generate.isPending} onClick={run}>
            <Sparkles className="h-4 w-4 me-1.5" /> {t("Generate draft", "إنشاء المسودة")}
          </Button>
        </div>
      )}
    </StepShell>
  );
}

/* -------------------------- Step 4: Review -------------------------- */

function ReviewStep({
  state,
  hasDraftOrApproved,
  approved,
  isAr,
  onOpenReview,
}: {
  state: StepState;
  hasDraftOrApproved: boolean;
  approved: boolean;
  isAr: boolean;
  onOpenReview: () => void;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  return (
    <StepShell stepKey="review" state={state} icon={<ClipboardCheck className="h-3.5 w-3.5" />} title={t("Review and approve", "المراجعة والاعتماد")}>
      {!hasDraftOrApproved ? (
        <p className="text-xs text-muted-foreground" data-testid="weekly-report-review-locked-note">
          {t("Opens after the draft is generated.", "يُفتح بعد إنشاء المسودة.")}
        </p>
      ) : (
        <div>
          <Badge variant="secondary" className="text-[10px]" data-testid="weekly-report-review-status">
            {approved ? t("Approved", "معتمد") : t("Draft ready", "المسودة جاهزة")}
          </Badge>
          <div className="mt-2">
            <Button size="sm" className="rounded-full" data-testid="weekly-report-open-review" onClick={onOpenReview}>
              {approved ? t("Open approved report", "فتح التقرير المعتمد") : t("Review draft", "مراجعة المسودة")}
              <ChevronRight className="h-4 w-4 ms-1" />
            </Button>
          </div>
        </div>
      )}
    </StepShell>
  );
}
